import { useAtomValue } from "jotai";
import { Suspense } from "react";
import pluralize from "pluralize";
import { Text, Icon } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import {
  formsSubmissionsAtomFamily_SUSPENDS,
  isPublicAtom,
  hasZslAtom,
  showFormSubmissionCountAtom,
} from "@zapier/canvas-state";
import styles from "./FormsSubmissionsCount.module.css";

type Props = {
  nodeId: string;
  interfaceProjectId: string | undefined;
  interfacePageId: string | undefined;
};

type InnerProps = {
  nodeId: string;
  interfaceProjectId: string;
  interfacePageId: string;
};

function SuspendedFormsSubmissionsCount({
  nodeId,
  interfaceProjectId,
  interfacePageId,
}: InnerProps) {
  const formSubmissions = useAtomValue(
    formsSubmissionsAtomFamily_SUSPENDS({
      nodeId,
      interfaceProjectId,
      interfacePageId,
    }),
  );

  if (formSubmissions !== null && formSubmissions.period > 0) {
    return (
      <Tooltip placement="top" content="Last 30 days">
        <Tooltip.FocusableTrigger>
          <div className={styles.contentWrapper}>
            <Icon name="fileAdd" size={16} color="TextWeaker" />
            <Text type="MinimalPrint2" color="TextWeaker">
              {`${formSubmissions.period.toLocaleString()} ${pluralize("submission", formSubmissions.period)}`}
            </Text>
          </div>
        </Tooltip.FocusableTrigger>
      </Tooltip>
    );
  }

  return null;
}

export function FormsSubmissionsCount({
  nodeId,
  interfaceProjectId,
  interfacePageId,
}: Props) {
  const isPublic = useAtomValue(isPublicAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const showFormSubmissionCount = useAtomValue(showFormSubmissionCountAtom);

  if (
    !showFormSubmissionCount ||
    isPublic ||
    hasZsl ||
    !interfaceProjectId ||
    !interfacePageId
  ) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <SuspendedFormsSubmissionsCount
        nodeId={nodeId}
        interfaceProjectId={interfaceProjectId}
        interfacePageId={interfacePageId}
      />
    </Suspense>
  );
}
